import { formatChemicalCompoundNameAsUnicode } from "@omnia/shared/utils/format"
import { defineDirective, FieldViewModel, groupBy, IOmniaShell, selectMany } from "../../../../omnia"
import { IBatchEditResults } from "../../../../requirements/edit/batch/types"
import { EditCtrl } from "../../../../requirements/edit/edit"
import { NutrientViewModel } from "../../../../requirements/edit/nutrient-vm"
import { ReqZoneVM } from "../../../../requirements/edit/req-zone-vm"
import { IFieldsService, INutrientGroup, INutritionService, NutrientGroup, NutrientType } from "../../../../types"
import { MapContext } from "../../map-context"
import { MapFilter } from "../../map-filter"
import { DashboardSelection } from "../../selection"
import { IDashboardContext, IEditRequirementsArguments } from "../../types"
import { RequirementsMapView } from "../../view/requirements"


export class MapEditCtrl extends EditCtrl {

    static create(): ng.IDirective {
        return defineDirective(MapEditCtrl, "app/nutrition/dashboard/map/requirements/edit/edit.html", {
            context: "="
        });
    }

    public context: IDashboardContext;

    private mapCtxt: MapContext;
    private mapFilter: MapFilter;
    private mapFilterDispose: Function;

    private _selectionWatcher: Function;

    private _filteredCropping: FieldViewModel[];

    public zoneOptions: {
        text: string,
        disabled: boolean,
        zone: ReqZoneVM;
    }[];

    private _selectWholeField: boolean;


    constructor(
        protected $scope: ng.IScope,
        protected svc: INutritionService,
        protected fieldSvc: IFieldsService,
        protected shell: IOmniaShell) {

        super($scope, svc, fieldSvc, shell);

    }

    $onInit() {

        this.mapCtxt = this.context.mapCtxt;
        this.mapFilter = this.context.mapFilter;

        this.setup().then(() => {

            this.mapFilterDispose = this.mapFilter.addProvider(() => this._filteredCropping);

            let action = this.context.getCurrentAction();

            let args: IEditRequirementsArguments = action.args;
            let cropping = args.cropping;

            this.initCropping(cropping, args.wholeField);

        });

    }

    protected dispose() {
        if (this.mapFilterDispose) {
            this.mapFilterDispose();
            this.mapFilterDispose = undefined;
        }
    }

    get selection(): DashboardSelection {
        if (this.context) {
            return this.context.selection;
        }
    }

    set selection(value: DashboardSelection) {
        this.context.setSelection(value, "MapEditCtrl");
    }

    protected setupCurrentCropping(cropping: FieldViewModel) {
        super.setupCurrentCropping(cropping);
        this.setupSelectOptions();
    }

    private setupSelectOptions() {

        let zones = this.getVisibleZones();

        let groups = groupBy(zones, z => z.nutrient ? getNutrientAsGroup(z.nutrient) : z.nutrientGroup)
            .sort((g1, g2) => {
                let diff = (g1.key == NutrientGroup.AllNutrients ? 0 : 1) - (g2.key == NutrientGroup.AllNutrients ? 0 : 1);
                if (!diff) diff = g1.key - g2.key;
                return diff;
            });

        this.zoneOptions = selectMany(groups.map(g => {

            let text = getNutrientGroupName(g.key)
            text = formatChemicalCompoundNameAsUnicode(text);

            let options = [];

            if (g.key != NutrientGroup.AllNutrients) {
                options.push({
                    text: text,
                    disabled: true,
                    zone: null,
                });
            }

            options = options.concat(g.values.map(z => {
                return {
                    text: z.description,
                    disabled: false,
                    zone: z
                }
            }));

            return options;

        }));

    }

    private initCropping(cropping: FieldViewModel[], wholeField?: boolean) {

        if (this._selectionWatcher) {
            this._selectionWatcher();
        }

        let s = this.selection;

        if (!(s && s.croppingId)) {
            let croppingId = cropping[0].croppingId;
            this.selection = this.selection.openCropping(croppingId);
            this.mapCtxt.selectCropping(croppingId);
        }

        this._selectWholeField = wholeField;

        this._selectionWatcher = this.$scope.$watch("vm.context.selection", () => this.applySelection());

    }

    private applySelection() {

        let s = this.selection;
        if (!s) return;

        if ((!this.cropping) || (this.cropping.croppingId != s.croppingId)) {
            let cropping = this.fieldSvc.findCropping(s.croppingId);
            this.setupCurrentCropping(cropping);
        }

        let zone: ReqZoneVM;
        let zones = this.getVisibleZones();

        if ((!this._selectWholeField) && s.zoneNumber) {
            zone = zones.find(z => z.zoneNumber == s.zoneNumber);
        }

        zone = zone || zones.find(z => z.wholeField) || this.selectedZone;

        this._selectWholeField = undefined;
        this.selectedZone = zone;

    }

    private selectMapViewForZone(zone: ReqZoneVM) {

        let nutrients = this.context.nutrients;

        let currentNutrient = nutrients.nutrient;

        if (zone.nutrients.some(n => n.nutrientType == currentNutrient.nutrient)) {
            return;
        }

        let currentGroup = nutrients.nutrientGroup;

        let targetNutrient = this.findNutrientInGroup(currentGroup, zone);

        if (!targetNutrient) {

            let groups = nutrients.nutrientGroups.sort((g1, g2) => g1.nutrients.length - g2.nutrients.length);

            currentGroup = groups.find(g => !!this.findNutrientInGroup(g, zone));

            if (currentGroup) {
                nutrients.nutrientGroup = currentGroup;
                targetNutrient = this.findNutrientInGroup(currentGroup, zone);
            }
        }

        if (targetNutrient) {
            nutrients.nutrient = targetNutrient;
        }

    }

    private findNutrientInGroup(group: INutrientGroup, zone: ReqZoneVM) {
        let targetNutrient = group.nutrients.find(n1 => zone.nutrients.some(n2 => n2.nutrientType == n1.nutrient));
        return targetNutrient;
    }


    public selectZone(zone: ReqZoneVM) {

        if (zone == this.selectedZone) return;

        super.selectZone(zone);

        let s = this.selection = this.selection.openCropping(zone.croppingId);

        if (!zone.zoneNumber) {

            // If the current cropping has a single zone,
            // this is a whole field zone,
            // so avoid reselecting the cropping in that case
            if (this.zones && (this.zones.length > 1)) {
                this.mapCtxt.selectCropping(zone.croppingId, false)
            }

            return

        }

        s = this.selection = s.selectZone(zone.zoneNumber);

        this.selectMapViewForZone(zone);
        this.mapCtxt.selectZoneOnCurrentView(s.croppingId, zone.zoneNumber);

    }

    protected updateOnZoneChanged(zone: ReqZoneVM, nutrient: NutrientViewModel) {

        let changes = zone.getNutrients(ntr => true, {}, ntr => ntr.value);

        if (!Object.keys(changes).length) {
            changes = undefined;
        }

        let views = this.mapCtxt.views.filter(v => v instanceof RequirementsMapView);

        views.forEach(v => {
            v.setZoneOverride(zone.id, changes);
        })

        if (nutrient && this.context.nutrients) {
            this.context.nutrients.setNutrient(nutrient.key);
        }

    }

    protected updateOnSaved() {

        let zone = this.selectedZone;

        super.updateOnSaved();

        this.context.planReader.refresh();

        this.mapCtxt.refreshViews();

        if (zone) {
            // Note: the selection has to be restored even if though has not changed,
            // to allow the underlying select interaction to release the former features,
            // and track the new ones
            this.mapCtxt.selectZoneOnCurrentView(this.cropping.croppingId, zone.zoneNumber);
        }

    }

    protected applyBatchEdit(edit: IBatchEditResults) {

        let ntrGroup = this.context.nutrients.nutrientGroup;
        let ntr = this.context.nutrients.nutrient;

        super.applyBatchEdit(edit);

        this.context.nutrients.nutrientGroup = ntrGroup;
        this.context.nutrients.nutrient = ntr;

    }

    protected cancelChanges() {
        let zone = this.selectedZone;
        this.mapCtxt.clearZoneOverrides();
        super.cancelChanges();
        zone = this.zones.find(z => z.zoneNumber == zone.zoneNumber);
        this.selectZone(zone);
    }

    protected close() {
        this.mapCtxt.clearZoneOverrides()
        this.context.endCurrentAction()
    }

}

const nutrientGroupNames = {
    [NutrientGroup.NS]: "N, SO3",
    [NutrientGroup.PK]: "P2O5, K2O, MgO",
    [NutrientGroup.Lime]: "Lime",
    [getNutrientAsGroup(NutrientType.P2O5)]: "P2O5",
    [getNutrientAsGroup(NutrientType.K2O)]: "K2O",
    [getNutrientAsGroup(NutrientType.MgO)]: "MgO"
}

function getNutrientGroupName(g: NutrientGroup) {
    return nutrientGroupNames[g] || (`${g}?`)
}

function getNutrientAsGroup(n: NutrientType) {
    return 1 << (n - 1)
}
